import { IconCheck } from "@tabler/icons-react";
import i18next from "i18next";
import { PerimetryTestType } from "../../../../model/TestType";
import { ThemeIcon } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { isVersionEnoughForNewPatterns, isVersionEnoughForSwapAndKinetic } from "@utils/CommonUtil";
import { useSelector } from "react-redux";
import { selectScopeList } from "@utils/redux-utils/selectors/productSelectors";
import { hasVfaPattern24_2C } from "@utils/VFA/pattern24_2CScope";
import { OptionalVFATests, VFABehaviour } from "../../../../types/testTypes";
import { HeadsetVersion } from "types/productTypes";

export const useFormComponentsData = (currentVersion = HeadsetVersion.n_a) => {
  const { t } = useTranslation();
  const scopeList = useSelector(selectScopeList);

  const testTypeData: any = [
    {
      group: "Standard",
      items: [
        {
          value: PerimetryTestType.PER_10_2,
          label: PerimetryTestType.PER_10_2,
        },
        {
          value: PerimetryTestType.PER_24_2,
          label: PerimetryTestType.PER_24_2,
        },
        ...(hasVfaPattern24_2C(scopeList)
          ? [{ value: PerimetryTestType.PER_24_2C, label: PerimetryTestType.PER_24_2C }]
          : []),
        {
          value: PerimetryTestType.PER_30_2,
          label: PerimetryTestType.PER_30_2,
        },
      ],
    },
  ];

  if (isVersionEnoughForNewPatterns(currentVersion)) {
    testTypeData.push(
      {
        group: "Screening",
        items: [
          {
            value: PerimetryTestType.PER_C_40,
            label: PerimetryTestType.PER_C_40,
          },
          {
            value: PerimetryTestType.PER_C_76,
            label: PerimetryTestType.PER_C_76,
          },
          {
            value: PerimetryTestType.PER_FF_120,
            label: PerimetryTestType.PER_FF_120,
          },
        ],
      },
      {
        group: "Ptosis",
        items: [
          {
            value: PerimetryTestType.PER_SUPERIOR36,
            label: "Superior 36",
          },
          {
            value: PerimetryTestType.PER_SUPERIOR64,
            label: "Superior 64",
          },
        ],
      },
      {
        group: "Binocular",
        items: [
          {
            value: PerimetryTestType.PER_BINOCULAR,
            label: "Esterman-1 (120°)",
          },
          {
            value: PerimetryTestType.PER_BINOCULAR2,
            label: "Esterman-2 (160°)",
          },
          {
            value: PerimetryTestType.PER_BINOCULAR3,
            label: "Esterman-3 (140°)",
          },
        ],
      },
    );
  } else {
    testTypeData.push(
      {
        group: "Screening",
        items: [
          {
            value: PerimetryTestType.PER_C_40,
            label: PerimetryTestType.PER_C_40,
          },
          {
            value: PerimetryTestType.PER_FF_120,
            label: PerimetryTestType.PER_FF_120,
          },
        ],
      },
      {
        group: "Binocular",
        items: [
          {
            value: PerimetryTestType.PER_BINOCULAR,
            label: PerimetryTestType.PER_BINOCULAR,
          },
        ],
      },
    );
  }

  const testBehaviourData = [
    {
      group: t("TestGroup.threshold"),
      items: [
        {
          value: VFABehaviour.TOP,
          label: i18next.t("TestController.testBehaviour_" + VFABehaviour.TOP),
        },
        {
          value: VFABehaviour.ADAPTIVE_TRACKING,
          label: i18next.t("TestController.testBehaviour_" + VFABehaviour.ADAPTIVE_TRACKING),
        },
        {
          value: VFABehaviour.SITA_LIKE,
          label: i18next.t("TestController.testBehaviour_" + VFABehaviour.SITA_LIKE),
        },
        {
          value: VFABehaviour.FULL_THRESH_4,
          label: i18next.t("TestController.testBehaviour_" + VFABehaviour.FULL_THRESH_4),
        },
      ],
    },
    {
      group: t("TestGroup.suprathreshold"),
      items: [
        {
          value: VFABehaviour.SIMPLE_TEST,
          label: i18next.t("TestController.testBehaviour_" + VFABehaviour.SIMPLE_TEST),
        },
        {
          value: VFABehaviour.TWO_ZONE,
          label: i18next.t("TestController.testBehaviour_" + VFABehaviour.TWO_ZONE),
        },
        {
          value: VFABehaviour.THREE_ZONE,
          label: i18next.t("TestController.testBehaviour_" + VFABehaviour.THREE_ZONE),
        },
      ],
    },
  ];

  // Add Optional VFA Tests if they're in the scope list
  if (scopeList && scopeList.length > 0) {
    if (
      scopeList.includes(OptionalVFATests.SWAP) &&
      isVersionEnoughForSwapAndKinetic(currentVersion)
    ) {
      const blueOnYellow = {
        group: t("TestGroup.blueOnYellow"),
        items: [],
      };
      blueOnYellow.items.push({
        value: VFABehaviour.SWAP_TOP,
        label: i18next.t("TestType." + OptionalVFATests.SWAP),
      });
      testBehaviourData.push(blueOnYellow);
    }
  }

  const testStartingWarningData = [
    {
      text: t("TestStarting.checkIfScreenIsOnWarning"),
      icon: (
        <ThemeIcon radius={"lg"} color="green">
          <IconCheck />
        </ThemeIcon>
      ),
    },
    {
      text: t("TestStarting.putDeviceOnPatientWarning"),
      icon: (
        <ThemeIcon radius={"lg"} color="green">
          <IconCheck />
        </ThemeIcon>
      ),
    },
    {
      text: t("TestStarting.pressTriggerButtonWarning"),
      icon: (
        <ThemeIcon radius={"lg"} color="green">
          <IconCheck />
        </ThemeIcon>
      ),
    },
    {
      text: t("TestStarting.checkWifiIconGreenWarning"),
      icon: (
        <ThemeIcon radius={"lg"} color="green">
          <IconCheck />
        </ThemeIcon>
      ),
    },
    {
      text: t("TestStarting.checkIfDeviceIsConnectedWarning"),
      icon: (
        <ThemeIcon radius={"lg"} color="green">
          <IconCheck />
        </ThemeIcon>
      ),
    },
  ];

  const testTerminatingWarningData = [
    {
      text: t("TestStarting.checkIfScreenIsOnWarning"),
      icon: (
        <ThemeIcon radius={"lg"} color="green">
          <IconCheck />
        </ThemeIcon>
      ),
    },
    {
      text: t("TestStarting.waitForTestToFinishWarning"),
      icon: (
        <ThemeIcon radius={"lg"} color="green">
          <IconCheck />
        </ThemeIcon>
      ),
    },
    {
      text: t("TestStarting.restartIfNothingHappensWarning"),
      icon: (
        <ThemeIcon radius={"lg"} color="orange">
          <IconCheck />
        </ThemeIcon>
      ),
    },
  ];

  return {
    testTypeData,
    testBehaviourData,
    testStartingWarningData,
    testTerminatingWarningData,
  };
};
export default useFormComponentsData;
